import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const Inventory = () => {
    const [inventory, setInventory] = useState([]);
    const [requirements, setRequirements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editingItem, setEditingItem] = useState(null); // Item being edited
    const [newQty, setNewQty] = useState('');
    const [userRole, setUserRole] = useState('');
    const [userData, setUserData] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, reqId: null });
    const [warningModal, setWarningModal] = useState(false);

    const ROOT_API = 'https://rotica.in';
    const BACKEND_API = 'https://rotica.in';
    const RAW_MATERIAL_THRESHOLD = 50;

    const fetchInventory = async () => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;
            const user = JSON.parse(userStr);
            setUserData(user);
            setUserRole(user.role?.toLowerCase() || '');

            const res = await axios.get(`${ROOT_API}/get_inventory.php?user_id=${user.id}&role=${user.role}`);
            if (res.data.success) {
                setInventory(res.data.inventory);
            }

            // Also fetch FOFO requirements if manufacturer
            if (user.role?.toLowerCase() === 'manufacturer') {
                fetchRequirements(user);
            }
        } catch (error) {
            console.error("Error fetching inventory:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchRequirements = async (user) => {
        try {
            const res = await axios.get(`${BACKEND_API}/get_fofo_requirements.php`, {
                params: { user_id: user.id, role: user.role }
            });
            if (res.data.success) {
                setRequirements(res.data.requirements);
            }
        } catch (error) {
            console.error("Error fetching requirements:", error);
        }
    };

    useEffect(() => {
        fetchInventory();
    }, []);

    const handleEditClick = (item) => {
        setEditingItem(item);
        // Reset qty
        setNewQty(0);
    };

    const handleUpdate = async () => {
        if (!editingItem) return;
        const isManufacturer = userRole === 'manufacturer';

        try {
            const userStr = localStorage.getItem('user');
            const user = JSON.parse(userStr);

            if (isManufacturer) {
                // Restriction: Can only request if stock <= threshold + 50
                const currentStock = editingItem.stock_qty || 0;
                const limit = RAW_MATERIAL_THRESHOLD + 50;

                if (currentStock > limit) {
                    setWarningModal(true);
                    return;
                }

                // Request Workflow
                const formData = new FormData();
                formData.append('product_id', editingItem.product_id);
                formData.append('entity_type', 'manufacturer');
                formData.append('entity_id', user.id);
                formData.append('required_qty', newQty);

                const res = await axios.post(`${BACKEND_API}/submit_fofo_requirement.php`, formData);
                if (res.data.success) {
                    setEditingItem(null);
                    fetchRequirements(user);
                    alert("Request submitted to owner for approval.");
                } else {
                    alert("Failed to submit request: " + res.data.message);
                }
            } else {
                // Direct Update (Distributor)
                const params = new URLSearchParams();
                params.append('product_id', editingItem.product_id);
                params.append('stock_qty', newQty);
                params.append('user_id', user.id);
                params.append('role', user.role);

                const res = await axios.post(`${ROOT_API}/update_inventory.php`, params);
                if (res.data.success) {
                    setEditingItem(null);
                    fetchInventory();
                    alert("Stock updated successfully!");
                } else {
                    alert("Failed to update: " + res.data.message);
                }
            }
        } catch (error) {
            console.error(error);
            alert("Error processing update. Please check network.");
        }
    };

    const showConfirmModal = (reqId) => {
        setConfirmModal({ show: true, reqId });
    };

    const handleConfirmReceipt = async () => {
        const reqId = confirmModal.reqId;
        setConfirmModal({ show: false, reqId: null });

        try {
            const formData = new FormData();
            formData.append('id', reqId);
            formData.append('status', 'closed');
            formData.append('role', 'manufacturer');

            const res = await axios.post(`${BACKEND_API}/update_fofo_status.php`, formData);
            if (res.data.success) {
                fetchInventory();
                alert("Inventory updated!");
            } else {
                alert(res.data.message);
            }
        } catch (error) {
            console.error(error);
            alert("Failed to confirm receipt");
        }
    };

    // Chart Data
    const isManufacturer = userRole === 'manufacturer';
    const chartData = {
        labels: inventory.map(i => i.base_name),
        datasets: [{
            label: isManufacturer ? 'Raw Material Level' : 'Stock Level',
            data: inventory.map(i => i.stock_qty),
            backgroundColor: inventory.map(i => i.stock_qty < RAW_MATERIAL_THRESHOLD ? '#e74c3c' : '#2ecc71'), // Red if low stock
            borderRadius: 6
        }]
    };

    // Get unit label based on product type (for manufacturers)
    const getUnit = (item) => {
        if (!isManufacturer) return 'Units';
        if (item.base_name?.toLowerCase().includes('premix')) return 'kg';
        if (item.base_name?.toLowerCase().includes('oil')) return 'liters';
        return 'Units';
    };

    if (loading) return <div className="loading-state">Loading Inventory...</div>;

    return (
        <div className="inventory-page">
            <header className="page-header">
                <h1>{isManufacturer ? 'Raw Materials Inventory' : 'Inventory Management'}</h1>
                <p className="subtitle">
                    {isManufacturer
                        ? 'Track and request raw material stock (Premix & Oil)'
                        : 'Track and update stock levels'}
                </p>
            </header>

            {/* Inventory Chart */}
            <div className="chart-card wide" style={{ marginBottom: '30px' }}>
                <h3>{isManufacturer ? 'Current Raw Material Levels' : 'Current Stock Levels'}</h3>
                <div className="chart-wrapper" style={{ height: '300px' }}>
                    <Bar
                        data={chartData}
                        options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            scales: {
                                y: { beginAtZero: true }
                            }
                        }}
                    />
                </div>
            </div>

            {/* Inventory Table */}
            <div className="table-card">
                <h3>{isManufacturer ? 'Available Raw Materials' : 'Inventory Items'}</h3>
                <div className="table-wrapper">
                    <table className="orders-table">
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>{isManufacturer ? 'Material Name' : 'Product Name'}</th>
                                <th>Specifications</th>
                                <th>{isManufacturer ? 'Existing Quantity' : 'Stock Qty'}</th>
                                <th>Last Updated</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {inventory.length > 0 ? (
                                inventory.map(item => (
                                    <tr key={item.product_id}>
                                        <td>#{item.product_id}</td>
                                        <td style={{ fontWeight: 600 }}>{item.base_name}</td>
                                        <td style={{ fontSize: '13px', color: '#666' }}>
                                            {item.weight_spec} / {item.size_spec}
                                        </td>
                                        <td>
                                            <span className={`stock-badge ${(item.stock_qty || 0) < RAW_MATERIAL_THRESHOLD ? 'low' : 'good'}`}>
                                                {item.stock_qty || 0} {getUnit(item)}
                                            </span>
                                        </td>
                                        <td style={{ fontSize: '13px', color: '#888' }}>
                                            {item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}
                                        </td>
                                        <td>
                                            <button
                                                className="action-btn edit-btn"
                                                onClick={() => handleEditClick(item)}
                                            >
                                                {isManufacturer ? 'Request Stock' : 'Update Stock'}
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>No inventory records found.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Manufacturer Requests Table */}
            {isManufacturer && (
                <div className="table-card" style={{ marginTop: '30px' }}>
                    <h3>Recent Material Requests</h3>
                    <div className="table-wrapper">
                        <table className="orders-table">
                            <thead>
                                <tr>
                                    <th>Material</th>
                                    <th>Qty Requested</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requirements.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No requests yet</td>
                                    </tr>
                                ) : (
                                    requirements.map(req => (
                                        <tr key={req.id}>
                                            <td>{req.product_name}</td>
                                            <td>{req.required_qty || 0}</td>
                                            <td>
                                                <span className={`status-badge ${req.status}`}>
                                                    {req.status.replace(/_/g, ' ')}
                                                </span>
                                            </td>
                                            <td>{new Date(req.created_at).toLocaleDateString()}</td>
                                            <td>
                                                {req.status === 'out for delivery' && (
                                                    <button
                                                        className="action-btn complete-btn"
                                                        onClick={() => showConfirmModal(req.id)}
                                                        style={{ backgroundColor: '#2ecc71', color: 'white' }}
                                                    >
                                                        Confirm Receipt
                                                    </button>
                                                )}
                                                {req.status === 'closed' && (
                                                    <span style={{ color: '#2ecc71', fontSize: '12px' }}>Stock Received ✓</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {editingItem && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <h3>{isManufacturer ? 'Request' : 'Update'} Stock: {editingItem.base_name}</h3>
                        <div className="form-group">
                            <label htmlFor="stockQty">{isManufacturer ? 'Quantity Required' : 'New Quantity'}</label>
                            <input
                                id="stockQty"
                                name="stockQty"
                                type="number"
                                className="modal-input"
                                value={newQty}
                                onChange={(e) => setNewQty(e.target.value)}
                            />
                        </div>
                        <div className="modal-actions">
                            <button className="cancel-btn" onClick={() => setEditingItem(null)}>Cancel</button>
                            <button className="save-btn" onClick={handleUpdate}>
                                {isManufacturer ? 'Submit Request' : 'Save Update'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Confirmation Modal */}
            {confirmModal.show && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ textAlign: 'center', maxWidth: '400px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>📦</div>
                        <h3 style={{ marginBottom: '10px' }}>Confirm Receipt</h3>
                        <p style={{ color: '#666', marginBottom: '25px' }}>
                            Do you confirm order received?<br />
                            <small>This will update your inventory stock.</small>
                        </p>
                        <div className="modal-actions" style={{ justifyContent: 'center', gap: '15px' }}>
                            <button
                                className="cancel-btn"
                                onClick={() => setConfirmModal({ show: false, reqId: null })}
                                style={{ padding: '10px 25px' }}
                            >
                                No, Cancel
                            </button>
                            <button
                                className="save-btn"
                                onClick={handleConfirmReceipt}
                                style={{ padding: '10px 25px', backgroundColor: '#2ecc71' }}
                            >
                                Yes, Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Warning Modal */}
            {warningModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ textAlign: 'center', maxWidth: '400px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '15px' }}>⚠️</div>
                        <h3 style={{ marginBottom: '10px', color: '#e74c3c' }}>Stock Request Denied</h3>
                        <p style={{ color: '#666', marginBottom: '25px' }}>
                            Please utilize existing stock before requesting more.<br />
                            <small>You have sufficient stock ({editingItem?.stock_qty || 0}) available.</small>
                        </p>
                        <div className="modal-actions" style={{ justifyContent: 'center' }}>
                            <button
                                className="save-btn"
                                onClick={() => setWarningModal(false)}
                                style={{ padding: '10px 25px', backgroundColor: '#e74c3c' }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Inventory;
